import React, { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X } from 'lucide-react';

/**
 * TurnstileModal — muncul saat user klik Submit.
 * Menampilkan Cloudflare Turnstile widget explicit di atas overlay.
 * Setelah verified, langsung panggil onVerified(token) dan tutup modal.
 *
 * Props:
 *   open        : boolean — tampilkan modal
 *   siteKey     : string  — Turnstile site key
 *   onVerified  : (token) => void — dipanggil setelah verifikasi sukses
 *   onClose     : () => void — dipanggil saat user tutup manual
 *   onError     : () => void — dipanggil saat Turnstile error/unavailable
 */
export const TurnstileModal = ({ open, siteKey, onVerified, onClose, onError }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifiedRef = useRef(onVerified);
  const onErrorRef = useRef(onError);

  // keep latest callbacks
  useEffect(() => {
    onVerifiedRef.current = onVerified;
    onErrorRef.current = onError;
  });

  // Reset & render widget setiap kali modal dibuka
  const renderWidget = useCallback(() => {
    if (!window.turnstile || !containerRef.current) return;
    // Hapus widget lama kalau ada
    if (widgetIdRef.current !== null) {
      try { window.turnstile.remove(widgetIdRef.current); } catch (e) { /* noop */ }
      widgetIdRef.current = null;
    }
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      theme: 'light',
      retry: 'auto',
      callback: (token) => {
        onVerifiedRef.current && onVerifiedRef.current(token);
      },
      'expired-callback': () => {
        // Token expired — reset widget agar user bisa coba lagi
        if (widgetIdRef.current !== null) {
          try { window.turnstile.reset(widgetIdRef.current); } catch (e) { /* noop */ }
        }
      },
      'error-callback': () => {
        onErrorRef.current && onErrorRef.current();
      },
    });
  }, [siteKey]);

  useEffect(() => {
    if (!open || !siteKey) return;

    let pollId = null;

    const tryRender = () => {
      if (window.turnstile) {
        renderWidget();
      } else {
        if (!document.getElementById('cf-turnstile-script')) {
          const s = document.createElement('script');
          s.id = 'cf-turnstile-script';
          s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          s.async = true;
          s.defer = true;
          document.head.appendChild(s);
        }
        pollId = setInterval(() => {
          if (window.turnstile) {
            clearInterval(pollId);
            renderWidget();
          }
        }, 100);
      }
    };

    // Tunggu sebentar agar DOM container sudah mount
    const t = setTimeout(tryRender, 80);

    return () => {
      clearTimeout(t);
      clearInterval(pollId);
      // Hapus widget saat modal tutup
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [open, siteKey, renderWidget]);

  // Tutup saat klik backdrop
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose && onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="turnstile-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdrop}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
        >
          <motion.div
            key="turnstile-card"
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-sm rounded-3xl border border-border bg-card p-6 shadow-2xl"
          >
            {/* Tombol tutup */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              aria-label="Tutup"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header */}
            <div className="mb-4 flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary-soft text-primary">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-base font-bold text-foreground">Verifikasi dulu, ya</h3>
                <p className="text-xs text-muted-foreground">Sebentar — ini untuk memastikan bukan bot 🤖</p>
              </div>
            </div>

            {/* Turnstile widget container */}
            <div className="flex justify-center">
              <div ref={containerRef} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
